import { useState } from "react";
import { Link } from "react-router-dom";
import type { SavedOrder } from "@/hooks/useOrders";

interface OrdersListProps {
  orders: SavedOrder[];
}

const STATUS_STEPS = [
  { key: "pending",   label: "قيد المعالجة", icon: "ri-time-line" },
  { key: "confirmed", label: "تم التأكيد",   icon: "ri-checkbox-circle-line" },
  { key: "delivered", label: "تم التوصيل",   icon: "ri-truck-line" },
];

const STATUS_MAP: Record<string, { label: string; color: string; bg: string; border: string; icon: string; step: number }> = {
  pending:   { label: "قيد المعالجة", color: "text-amber-400",  bg: "bg-amber-400/10",  border: "border-amber-400/20",  icon: "ri-time-line",             step: 0 },
  confirmed: { label: "تم التأكيد",   color: "text-green-400",  bg: "bg-green-400/10",  border: "border-green-400/20",  icon: "ri-checkbox-circle-line",  step: 1 },
  shipped:   { label: "تم الشحن",     color: "text-indigo-400", bg: "bg-indigo-400/10", border: "border-indigo-400/20", icon: "ri-send-plane-line",       step: 1 },
  delivered: { label: "تم التوصيل",   color: "text-teal-400",   bg: "bg-teal-400/10",   border: "border-teal-400/20",   icon: "ri-truck-line",            step: 2 },
  cancelled: { label: "ملغي",          color: "text-red-400",    bg: "bg-red-400/10",    border: "border-red-400/20",    icon: "ri-close-circle-line",     step: -1 },
};

type FilterType = "all" | "pending" | "confirmed" | "shipped" | "delivered" | "cancelled";

export default function OrdersList({ orders }: OrdersListProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>("all");

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <div className="w-20 h-20 flex items-center justify-center bg-white/4 rounded-2xl">
          <i className="ri-shopping-bag-line text-gray-600 text-3xl"></i>
        </div>
        <div className="text-center">
          <p className="text-gray-400 text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
            لا توجد طلبات بعد
          </p>
          <p className="text-gray-600 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
            ابدأ تسوقك الآن واستمتع بأفضل المنتجات
          </p>
        </div>
        <a
          href="/"
          className="flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-black px-6 py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
          style={{ fontFamily: "'Cairo', sans-serif" }}
        >
          <i className="ri-store-2-line"></i>
          تصفح المتجر
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Filter Tabs */}
      <div className="flex gap-1.5 flex-wrap">
        {(["all", "pending", "confirmed", "shipped", "delivered", "cancelled"] as FilterType[]).map((f) => {
          const count = f === "all" ? orders.length : orders.filter((o) => o.status === f).length;
          if (f !== "all" && count === 0) return null;
          const labels: Record<FilterType, string> = {
            all: "الكل",
            pending: "قيد المعالجة",
            confirmed: "مؤكدة",
            shipped: "تم الشحن",
            delivered: "مُوصّلة",
            cancelled: "ملغية",
          };
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                filter === f
                  ? "bg-amber-400 text-black"
                  : "bg-[#1a1a1a] text-gray-400 hover:text-white border border-white/8"
              }`}
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              {labels[f]}
              {count > 0 && (
                <span className={`w-4 h-4 flex items-center justify-center rounded-full text-[10px] font-black ${
                  filter === f ? "bg-black/20 text-black" : "bg-white/10 text-gray-400"
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Orders List */}
      {filtered.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-gray-500 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>لا توجد طلبات في هذه الفئة</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((order) => {
            const st = STATUS_MAP[order.status] ?? STATUS_MAP["pending"];
            const isOpen = expanded === order.orderRef;
            const orderDate = new Date(order.date).toLocaleDateString("ar-SA", {
              year: "numeric", month: "short", day: "numeric",
            });
            const orderTime = new Date(order.date).toLocaleTimeString("ar-SA", {
              hour: "2-digit", minute: "2-digit",
            });

            return (
              <div
                key={order.orderRef}
                className="bg-[#1a1a1a] border border-white/8 rounded-2xl overflow-hidden transition-all"
              >
                {/* Status top bar */}
                <div className={`h-0.5 w-full ${
                  order.status === "delivered" ? "bg-gradient-to-r from-teal-400/0 via-teal-400/60 to-teal-400/0" :
                  order.status === "confirmed" ? "bg-gradient-to-r from-green-400/0 via-green-400/60 to-green-400/0" :
                  "bg-gradient-to-r from-amber-400/0 via-amber-400/60 to-amber-400/0"
                }`}></div>

                {/* Header row */}
                <button
                  onClick={() => setExpanded(isOpen ? null : order.orderRef)}
                  className="w-full flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-white/2 transition-colors text-right"
                >
                  {/* Order icon */}
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${st.bg} border ${st.border}`}>
                    <i className={`${st.icon} ${st.color} text-base`}></i>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white text-sm font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        #{order.orderRef}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${st.bg} ${st.border} ${st.color}`}>
                        <i className={`${st.icon} text-xs`}></i>
                        {st.label}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {orderDate} — {orderTime} · {order.city}
                    </p>
                  </div>

                  <div className="text-left flex-shrink-0">
                    <p className="text-amber-400 font-black text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {order.finalPrice.toLocaleString()} ر.س
                    </p>
                    <p className="text-gray-600 text-xs mt-0.5 text-left" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {order.items.length} {order.items.length === 1 ? "منتج" : "منتجات"}
                    </p>
                  </div>

                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                    <i className={`${isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-500 text-lg`}></i>
                  </div>
                </button>

                {/* Expanded details */}
                {isOpen && (
                  <div className="border-t border-white/5 px-5 py-5 flex flex-col gap-5">
                    <Link
                      to={`/order/${order.orderRef}`}
                      className="flex items-center justify-center gap-2 bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/30 text-amber-400 font-bold py-2.5 rounded-xl text-sm transition-all cursor-pointer"
                    >
                      <i className="ri-eye-line"></i>
                      عرض صفحة تفاصيل الطلب الكاملة
                    </Link>

                    {/* Status Tracker */}
                    <div>
                      <p className="text-gray-500 text-xs font-bold mb-3" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        <i className="ri-map-pin-time-line ml-1"></i>
                        تتبع الطلب
                      </p>
                      <div className="flex items-center gap-0">
                        {STATUS_STEPS.map((step, idx) => {
                          const isDone = st.step >= idx;
                          const isCurrent = st.step === idx;
                          return (
                            <div key={step.key} className="flex items-center flex-1">
                              <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                                <div className={`w-9 h-9 flex items-center justify-center rounded-full border-2 transition-all ${
                                  isDone
                                    ? isCurrent
                                      ? "bg-amber-400 border-amber-400 text-black"
                                      : "bg-green-400/20 border-green-400/50 text-green-400"
                                    : "bg-[#111] border-white/10 text-gray-600"
                                }`}>
                                  {isDone && !isCurrent ? (
                                    <i className="ri-check-line text-sm font-black"></i>
                                  ) : (
                                    <i className={`${step.icon} text-sm`}></i>
                                  )}
                                </div>
                                <span className={`text-[10px] font-bold text-center leading-tight ${
                                  isDone ? (isCurrent ? "text-amber-400" : "text-green-400") : "text-gray-600"
                                }`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                                  {step.label}
                                </span>
                              </div>
                              {idx < STATUS_STEPS.length - 1 && (
                                <div className={`flex-1 h-0.5 mx-1 mb-5 rounded-full ${
                                  st.step > idx ? "bg-green-400/40" : "bg-white/8"
                                }`}></div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Items */}
                    <div>
                      <p className="text-gray-500 text-xs font-bold mb-3" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        <i className="ri-box-3-line ml-1"></i>
                        المنتجات
                      </p>
                      <div className="flex flex-col gap-2">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3 bg-[#111] rounded-xl px-3 py-2.5">
                            <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-[#222]">
                              <img src={item.image} alt={item.nameAr} className="w-full h-full object-cover object-top" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-xs font-bold truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>
                                {item.nameAr}
                              </p>
                              <p className="text-gray-500 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                                الكمية: {item.qty}
                              </p>
                            </div>
                            <p className="text-amber-400 text-xs font-black flex-shrink-0" style={{ fontFamily: "'Cairo', sans-serif" }}>
                              {(item.price * item.qty).toLocaleString()} ر.س
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="bg-[#111] rounded-2xl px-4 py-4 flex flex-col gap-2.5">
                      <p className="text-gray-500 text-xs font-bold mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        <i className="ri-receipt-line ml-1"></i>
                        ملخص الطلب
                      </p>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>طريقة الدفع</span>
                        <span className="text-gray-300 text-xs font-bold flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                          <i className="ri-hand-coin-line text-amber-400/60"></i>
                          {order.paymentLabel}
                        </span>
                      </div>
                      {order.discountCode && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            كود الخصم
                            <span className="text-green-400 mr-1 font-black">({order.discountCode})</span>
                          </span>
                          <span className="text-green-400 text-xs font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            — {order.discountAmount.toLocaleString()} ر.س
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center border-t border-white/5 pt-2.5 mt-1">
                        <span className="text-white text-sm font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>الإجمالي</span>
                        <span className="text-amber-400 text-base font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
                          {order.finalPrice.toLocaleString()} ر.س
                        </span>
                      </div>
                    </div>

                    {/* Address */}
                    <div className="flex items-start gap-3 bg-[#111] rounded-xl px-4 py-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-400/10 rounded-lg flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-line text-amber-400 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs font-bold mb-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>عنوان التوصيل</p>
                        <p className="text-gray-300 text-xs leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                          {order.city} — {order.address}
                        </p>
                        {order.notes && (
                          <p className="text-gray-600 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            <i className="ri-sticky-note-line ml-1"></i>
                            {order.notes}
                          </p>
                        )}
                      </div>
                    </div>

                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
